import React from 'react';

/** The small line glyphs of the CENTERLINE AI design, drawn in the current text colour. */

interface GlyphProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}

const Stroke = (props: GlyphProps & { children: React.ReactNode }) => {
  const { size = 16, strokeWidth = 2, className, style, children } = props;
  return (
    <svg
      className={className}
      style={{ flexShrink: 0, ...style }}
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
};

export const PlusGlyph = (p: GlyphProps) => (
  <Stroke size={18} strokeWidth={2.2} {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Stroke>
);

export const MicGlyph = (p: GlyphProps) => (
  <Stroke size={18} strokeWidth={2} {...p}>
    <path d="M9 5a3 3 0 0 1 6 0v6a3 3 0 0 1-6 0z" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
    <path d="M8.5 21h7" />
  </Stroke>
);

export const CloseGlyph = (p: GlyphProps) => (
  <Stroke size={12} strokeWidth={2.6} {...p}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </Stroke>
);

export const ChevronRightGlyph = (p: GlyphProps) => (
  <Stroke size={14} strokeWidth={2.4} {...p}>
    <path d="M9 6l6 6-6 6" />
  </Stroke>
);

export const ChevronLeftGlyph = (p: GlyphProps) => (
  <Stroke size={20} {...p}>
    <path d="M15 6l-6 6 6 6" />
  </Stroke>
);

export const SendGlyph = (p: GlyphProps) => (
  <Stroke size={22} {...p}>
    <path d="M4 12l16-8-6 16-3-7z" />
    <path d="M11 13l9-9" />
  </Stroke>
);

export const StopGlyph = ({ size = 18 }: GlyphProps) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24">
    <path d="M6 6h12v12H6z" fill="currentColor" />
  </svg>
);

export const CopyGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M9 9h11v11H9z" />
    <path d="M5 15V5a1 1 0 0 1 1-1h9" />
  </Stroke>
);

export const SearchGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M11 5a6 6 0 1 0 0.01 0z" />
    <path d="M20 20l-4.6-4.6" />
  </Stroke>
);

export const CheckGlyph = (p: GlyphProps) => (
  <Stroke size={12} strokeWidth={3} {...p}>
    <path d="M5 12l5 5 9-10" />
  </Stroke>
);

export const ThinkingGlyph = (p: GlyphProps) => (
  <Stroke size={16} strokeWidth={1.8} {...p}>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3z" />
  </Stroke>
);

export const ChatGlyph = (p: GlyphProps) => (
  <Stroke size={28} strokeWidth={1.8} {...p}>
    <path d="M4 5h16v11H10l-6 4z" />
    <path d="M8 9h8" />
    <path d="M8 12h5" />
  </Stroke>
);

export const ThumbUpGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M7 11v9H4v-9z" />
    <path d="M7 11l4-7a2 2 0 0 1 2 2v4h5a2 2 0 0 1 2 2.3l-1.2 6a2 2 0 0 1-2 1.7H7" />
  </Stroke>
);

export const ThumbDownGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M7 13V4H4v9z" />
    <path d="M7 13l4 7a2 2 0 0 0 2-2v-4h5a2 2 0 0 0 2-2.3l-1.2-6a2 2 0 0 0-2-1.7H7" />
  </Stroke>
);

export const MoreGlyph = ({ size = 18 }: GlyphProps) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24">
    <g fill="currentColor">
      <path d="M5 10.5a1.5 1.5 0 1 0 0.01 0z" />
      <path d="M12 10.5a1.5 1.5 0 1 0 0.01 0z" />
      <path d="M19 10.5a1.5 1.5 0 1 0 0.01 0z" />
    </g>
  </svg>
);

export const RenameGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
  </Stroke>
);

export const DeleteGlyph = (p: GlyphProps) => (
  <Stroke size={16} {...p}>
    <path d="M5 7h14" />
    <path d="M9 7V4h6v3" />
    <path d="M7 7l1 13h8l1-13" />
  </Stroke>
);

/** The ring that turns while a step runs. */
export const SpinnerGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    role="img"
    aria-label="Running"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="3"
    strokeLinecap="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M12 3a9 9 0 1 0 0.01 0z" stroke="#E6EEFF" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="#0947B9" />
  </svg>
);

/** The person shown beside the user's questions. */
export const UserAvatar = () => (
  <svg aria-hidden="true" focusable="false" width="32" height="32" viewBox="0 0 24 24">
    <path d="M12 1a11 11 0 1 0 0.01 0z" fill="#C9C8C9" />
    <path d="M12 6a4 4 0 1 0 0.01 0z" fill="#FFFFFF" />
    <path d="M5 19c1.6-3 4.1-4.5 7-4.5s5.4 1.5 7 4.5" fill="#FFFFFF" />
  </svg>
);
